import { useState } from 'react'
import { STAGES, COACH_POINTS, REGISTRATION, SEASON_PRICING, AGE_BANDS, MAX_CHILDREN } from './content'
import { BalloonDiagram } from './BalloonDiagram'

type ChildRow = { school: string; age_band: string }

const blankChild = (): ChildRow => ({ school: '', age_band: '' })

// Empty in dev, so the vite proxy forwards /api to localhost:8000. Set to
// https://api.volleyquest.net at build time for production, where the BFF is
// a different origin and CORS applies (allow-list lives in app/main.py).
const API_BASE = import.meta.env.VITE_API_BASE ?? ''

function WaitlistForm() {
  const [email, setEmail] = useState('')
  const [children, setChildren] = useState<ChildRow[]>([blankChild()])
  const [error, setError] = useState('')
  const [sending, setSending] = useState(false)
  const [done, setDone] = useState(false)

  function setChild(i: number, patch: Partial<ChildRow>) {
    setChildren(children.map((c, n) => (n === i ? { ...c, ...patch } : c)))
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    const rows = children.map((c) => ({ ...c, school: c.school.trim() }))
    if (rows.some((c) => c.school.length < 2))
      return setError('Add the school, pre-K or daycare for each child.')
    if (rows.some((c) => !c.age_band))
      return setError('Pick an age group for each child.')

    setSending(true)
    try {
      const res = await fetch(`${API_BASE}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, children: rows }),
      })
      if (!res.ok) throw new Error(String(res.status))
      setDone(true)
    } catch {
      setError("That didn't save. Try again in a moment.")
    } finally {
      setSending(false)
    }
  }

  if (done) {
    return (
      <div className="success" role="status" aria-live="polite">
        <h3>You're on the waitlist.</h3>
        <p>We'll email you as soon as registration opens. The more families sign up
          from one school, the better the case for opening a group near it.</p>
      </div>
    )
  }

  return (
    <form onSubmit={submit} noValidate>
      {error && <p className="err">{error}</p>}
      <div className="field">
        <label htmlFor="email">Parent or guardian email</label>
        <input id="email" type="email" required autoComplete="email" placeholder="you@example.com"
          value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      {children.map((child, i) => (
        <fieldset className="child-row" key={i}>
          <span className="legend">
            {children.length > 1 ? `Child ${i + 1}` : 'My child'}
            {children.length > 1 && (
              <button type="button" className="link-btn"
                onClick={() => setChildren(children.filter((_, n) => n !== i))}>
                Remove
              </button>
            )}
          </span>
          <div className="field">
            <label htmlFor={`school-${i}`}>School, pre-K or daycare
              <span className="hint">Just so we know what area you're in.</span>
            </label>
            <input id={`school-${i}`} type="text" required maxLength={120}
              placeholder="Lincoln Elementary" value={child.school}
              onChange={(e) => setChild(i, { school: e.target.value })} />
          </div>
          <div className="chips" role="group" aria-label={`Age group for child ${i + 1}`}>
            {AGE_BANDS.map((a) => (
              <button key={a} type="button" className="chip"
                aria-pressed={child.age_band === a}
                onClick={() => setChild(i, { age_band: a })}>{a.replace('-', '–')}</button>
            ))}
          </div>
        </fieldset>
      ))}
      {children.length < MAX_CHILDREN && (
        <button type="button" className="link-btn add-child"
          onClick={() => setChildren([...children, blankChild()])}>
          + Add another child
        </button>
      )}
      <button className="btn" type="submit" disabled={sending}>
        {sending ? 'Joining…' : 'Join the waitlist'}
      </button>
    </form>
  )
}

export default function App() {

  return (
    <>
      <header className="wrap">
        <img className="mark-img" src="/logo.png" alt="Volley Quest — children's volleyball" width={580} height={640} />
      </header>

      <main>
        <section className="wrap hero">
          <div>
            <p className="eyebrow">Co-ed · Ages 3–12 · Coming soon</p>
            <h1>It starts with a <em>balloon</em>.</h1>
            <p className="lede">A three-year-old can't pass a volleyball. Any three-year-old
              can keep a balloon off the floor — and that's the same skill, moving slower.{' '}
              <strong>Volley Quest teaches volleyball backward from the ball up:</strong>{' '}
              balloon, then beach ball, then the real thing.</p>
            <div className="cta-row">
              <a className="btn" href="#waitlist">Join the waitlist</a>
              <span className="cta-note">Tell us your child's school so we know where to look first</span>
            </div>
          </div>
          <div className="dwrap"><BalloonDiagram /></div>
        </section>

        <section className="wrap stages">
          <div className="section-head">
            <p className="eyebrow">The progression</p>
            <h2>Three balls. Nine years. One sport.</h2>
            <p>Each stage uses a ball that moves at the speed a child that age can actually
              track. Nothing is watered down — the skills are real volleyball skills from day
              one, just at a pace the body can meet.</p>
          </div>
          <div className="stage-grid">
            {STAGES.map((s) => (
              <article className="stage" key={s.ages}>
                <p className="ages">{s.ages}</p>
                <h3>{s.title}</h3>
                <p>{s.blurb}</p>
                <ul className="skills">{s.skills.map((k) => <li key={k}>{k}</li>)}</ul>
              </article>
            ))}
          </div>
        </section>

        <section className="wrap coaches">
          <div className="section-head">
            <p className="eyebrow">The coaches</p>
            <h2>The grown-up in the gym matters as much as the ball.</h2>
            <p>Handing your three-year-old to a stranger for an hour is the real ask here, not
              the registration fee. So we are specific about who does the handing-back.</p>
          </div>
          <div className="coach-grid">
            {COACH_POINTS.map((c) => (
              <article className="coach" key={c.label}>
                <h3>{c.label}</h3>
                <p>{c.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="wrap pricing" id="pricing">
          <div className="section-head">
            <p className="eyebrow">What it costs</p>
            <h2>Ten weeks, twice a year.</h2>
            <p>Seasons run ten weeks — one in the fall, one in the spring. Registration is
              paid once a year and covers both of them; after that you choose how to pay
              for the season itself.</p>
          </div>
          <div className="price-grid">
            <article className="price reg">
              <h3>{REGISTRATION.label}</h3>
              <p className="amount">{REGISTRATION.amount}
                <span className="unit">{REGISTRATION.unit}</span></p>
              <p className="pnote">{REGISTRATION.note}</p>
            </article>
            {SEASON_PRICING.map((p) => (
              <article className={p.best ? 'price best' : 'price'} key={p.label}>
                <h3>{p.label}{p.best && <span className="badge">Best value</span>}</h3>
                <p className="amount">{p.amount}
                  <span className="unit">{p.unit}</span></p>
                <p className="pnote">{p.note}</p>
              </article>
            ))}
          </div>
          <p className="sib">Signing up more than one child? Sibling discounts are
            available — we’ll have the details when registration opens.</p>
        </section>

        <section className="wrap waitlist" id="waitlist">
          <div className="card">
            <div>
              <p className="eyebrow">Waitlist</p>
              <h2>Be first to know when registration opens.</h2>
              <p className="pitch">Where your child goes to school tells us which
                neighborhoods have enough interest to open a group. Their age group
                tells us how many spots each stage needs.</p>
              <p className="privacy">We ask for a parent or guardian's email, each child's
                school and their age group — never a child's name, birthdate or contact
                details. One email when registration opens, and nothing else.</p>
            </div>
            <div><WaitlistForm /></div>
          </div>
        </section>
      </main>

      <footer>
        <div className="wrap foot-row">
          <span>Volley Quest · Co-ed · Ages 3–12</span>
          <span className="store-tag">Store coming soon</span>
          <span><a href="mailto:spike@volleyquest.net">spike@volleyquest.net</a></span>
        </div>
      </footer>
    </>
  )
}
